/**
 * Parses a given value into a floating-point number.
 * If the value cannot be parsed as a number, a default value is returned.
 *
 * @param {string|number} v - The value to be parsed into a floating-point number.
 * @param {number} [returnIfCannotParse=0] - The value to return if the parsing fails.
 * @return {number} The parsed floating-point number, or the default value if parsing fails.
 */
export function _parseFloat (v: string|number, returnIfCannotParse = 0) {
  const parsed = parseFloat(`${v}`);
  if (isNaN(parsed)) {
    return returnIfCannotParse;
  }
  return parsed;
}

/**
 * Converts an amount to cents, no decimals
 * @param value
 * @private
 */
export function toCents(value: number) {
  return Math.round((value || 0) * 100);
}

/**
 * Rounds a number to two decimal places.
 *
 * This function uses `Number.EPSILON` to ensure accurate rounding of floating-point
 * numbers, preventing common precision errors (e.g., 1.005 rounding incorrectly).
 *
 * @param {number} value - The number to be rounded.
 * @returns {number} The value rounded to the nearest hundredth.
 *
 * @example
 * roundToTwoDecimals(10.567); // Returns 10.57
 * roundToTwoDecimals(10.5); // Returns 10.5
 * roundToTwoDecimals(1.005);// Returns 1.01
 */
export function roundToTwoDecimals(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
* Normalizes a raw decimal tax rate into a standardized percentage.
*
* This function first converts the decimal rate (e.g., 0.20) to a percentage (20),
* then rounds that percentage to the nearest 0.5 increment. This helps correct
* minor OCR deviations or align with standard tax rate steps.
*
* @param {number} rawRate - The raw decimal tax rate (e.g., 0.19, 0.205).
* @returns {number} The tax rate as a percentage, rounded to the nearest 0.5.
*
* @example
* normalizeTaxRate(0.20); // Returns 20 (20.0 rounded to 20.0)
* normalizeTaxRate(0.18699); // Returns 18.5 (18.699 rounded to nearest 0.5)
* normalizeTaxRate(0.052); // Returns 5 (5.2 rounded to 5.0)
* normalizeTaxRate(0.127); // Returns 12.5 (12.7 rounded to 12.5)
*/
export function normalizeTaxRate(rawRate: number): number {
  // Convert to percentage (18.699...)
  const percentage = rawRate * 100;

  // Round to nearest 0.5 (Standard for tax rates globally)
  const step = 0.5;
  return Math.round(percentage / step) * step;
}

/**
 * Parses a value into an integer. If the value cannot be parsed, returns a default fallback value.
 *
 * @param {string|number} v - The value to parse as an integer. This can be a string or a number.
 * @param {number} [returnIfCannotParse=0] - The fallback value to return if parsing fails or produces NaN.
 * @return {number} Returns the parsed integer, or the fallback value if parsing is unsuccessful.
 */
export function _parseInt (v: string|number, returnIfCannotParse = 0) {
  const parsed = parseInt(`${v}`);
  if (isNaN(parsed)) {
    return returnIfCannotParse;
  }
  return parsed;
}

